import React, { useEffect, useState } from "react";

import { WarningExclaimSvg } from "../../components/svg/WarningExclaimSvg";
import { formatCountdown } from "./sessionTimeout.utils";

type Props = {
  /** Milliseconds left before the automatic sign-out. */
  getRemainingTime: () => number;
  onStaySignedIn: () => void;
  onSignOut: () => void;
};

/**
 * Shown 3 minutes before the automatic sign-out after inactivity
 * (Roadmap 1800). Clicking outside or pressing Esc keeps the person signed in.
 */
export const SessionTimeoutWarning: React.FC<Props> = ({
  getRemainingTime,
  onStaySignedIn,
  onSignOut,
}) => {
  const [remaining, setRemaining] = useState(getRemainingTime());

  useEffect(() => {
    const interval = window.setInterval(
      () => setRemaining(getRemainingTime()),
      1000,
    );

    return () => window.clearInterval(interval);
  }, [getRemainingTime]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) =>
      event.key === "Escape" && onStaySignedIn();

    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onStaySignedIn]);

  return (
    <div
      className={"modal modal-open !z-[1000]"}
      role={"presentation"}
      onClick={onStaySignedIn}>
      <div
        role={"alertdialog"}
        aria-modal={"true"}
        aria-labelledby={"session-timeout-title"}
        className={
          "relative w-[440px] max-w-[calc(100vw-32px)] rounded-2xl bg-white p-6 font-Manrope shadow-modal"
        }
        onClick={(event) => event.stopPropagation()}>
        <div
          className={
            "mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFFBE5] text-[#FAAF0E]"
          }>
          <WarningExclaimSvg sizeCx={"h-6 w-6"} />
        </div>

        <p
          id={"session-timeout-title"}
          className={"mb-2 text-xl font-semibold text-[#1D2C3B]"}>
          Your session is about to expire
        </p>

        <p className={"text-sm text-[#5F758F]"}>
          You haven&apos;t been active for a while. For your security, you will
          be signed out in{" "}
          <span className={"font-bold tabular-nums text-[#1F2433]"}>
            {formatCountdown(remaining)}
          </span>
          .
        </p>

        <div className={"mt-8 flex gap-3 text-sm font-semibold"}>
          <button
            type={"button"}
            onClick={onSignOut}
            className={
              "h-10 w-full rounded-lg border border-[#C8D2DE] bg-white px-4 text-[#253747] hover:bg-[#F7F8FA]"
            }>
            Sign out
          </button>
          <button
            type={"button"}
            autoFocus
            onClick={onStaySignedIn}
            className={
              "h-10 w-full rounded-lg bg-[#6B4EFF] px-4 text-white hover:bg-[#5A3EEB]"
            }>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
};
